import launchDarklyService from "../services/launchdarkly";

const LaunchDarklyPlugin = {
  install(Vue) {
    // Make the service available globally
    Vue.prototype.$launchDarkly = launchDarklyService;

    // Add global methods for easy access
    Vue.prototype.$getFlag = async function (flagKey, defaultValue = false) {
      try {
        // Ensure LaunchDarkly is initialized before fetching flag
        if (!launchDarklyService.isInitialized) {
          await launchDarklyService.initialize();
        }
        return await launchDarklyService.getFlag(flagKey, defaultValue);
      } catch (error) {
        console.error(`Error getting flag "${flagKey}":`, error);
        return defaultValue;
      }
    };

    Vue.prototype.$getAllFlags = async function () {
      return await launchDarklyService.getAllFlags();
    };

    Vue.prototype.$updateUser = async function (user) {
      return await launchDarklyService.identify(user);
    };

    Vue.prototype.$getCurrentUser = function () {
      return launchDarklyService.getCurrentUser();
    };

    Vue.prototype.$isAnonymousUser = function () {
      return launchDarklyService.isAnonymousUser();
    };

    Vue.prototype.$hasUserContext = function () {
      return launchDarklyService.hasUserContext();
    };

    Vue.prototype.$resetToAnonymous = async function () {
      return await launchDarklyService.resetToAnonymous();
    };

    Vue.prototype.$identifyTenant = async function (user, account) {
      return await launchDarklyService.identifyTenant(user, account);
    };

    Vue.prototype.$updateUserWithAccount = async function (user, account) {
      return await launchDarklyService.updateUserWithAccount(user, account);
    };

    Vue.prototype.$flagValue = async function (flagKey, defaultValue = false) {
      return await this.$getFlag(flagKey, defaultValue);
    };

    Vue.prototype.$ifFlag = async function (flagKey, onTrue, onFalse, defaultValue = false) {
      const value = await this.$getFlag(flagKey, defaultValue);
      if (value) {
        return typeof onTrue === "function" ? onTrue() : onTrue;
      }
      return typeof onFalse === "function" ? onFalse() : onFalse;
    };

    Vue.prototype.$whenFlags = async function (flagsConfig, handler) {
      // flagsConfig: { 'flag.a': false, 'flag.b': 'Title' }
      const entries = await Promise.all(
        Object.entries(flagsConfig).map(async ([key, defVal]) => {
          const v = await this.$getFlag(key, defVal);
          return [key, v];
        })
      );
      const ctx = entries.reduce((acc, [k, v]) => {
        acc[k] = v;
        return acc;
      }, {});
      return handler(ctx);
    };

    // Dynamically load a module based on a feature flag
    // Usage: const mod = await this.$loadIfFlag('flag', () => import('A'), () => import('B'), false)
    Vue.prototype.$loadIfFlag = async function (
      flagKey,
      loadWhenTrue,
      loadWhenFalse,
      defaultValue = false
    ) {
      const on = await this.$getFlag(flagKey, defaultValue);
      const loader = on ? loadWhenTrue : loadWhenFalse;
      const mod = await (typeof loader === "function" ? loader() : null);
      return mod && mod.default ? mod.default : mod;
    };

    // Global mixin for reactive flag watching
    Vue.mixin({
      data() {
        return {
          flags: {},
          flagsLoading: false,
        };
      },
      methods: {
        // Watch a specific flag and update reactively
        async $watchFlag(flagKey, defaultValue = false) {
          this.$set(this.flags, flagKey, defaultValue);
          this.flagsLoading = true;

          try {
            const value = await this.$getFlag(flagKey, defaultValue);
            this.$set(this.flags, flagKey, value);
          } catch (error) {
            console.error(`Error watching flag ${flagKey}:`, error);
            this.$set(this.flags, flagKey, defaultValue);
          } finally {
            this.flagsLoading = false;
          }
        },

        // Watch multiple flags at once
        async $watchFlags(flagsConfig) {
          this.flagsLoading = true;

          try {
            const promises = Object.entries(flagsConfig).map(async ([key, defaultValue]) => {
              const value = await this.$getFlag(key, defaultValue);
              this.$set(this.flags, key, value);
              return { key, value };
            });

            await Promise.all(promises);
          } catch (error) {
            console.error("Error watching multiple flags:", error);
          } finally {
            this.flagsLoading = false;
          }
        },

        // Refresh all watched flags
        async $refreshFlags() {
          if (Object.keys(this.flags).length === 0) return;

          this.flagsLoading = true;

          try {
            const promises = Object.keys(this.flags).map(async (key) => {
              const value = await this.$getFlag(key, this.flags[key]);
              this.$set(this.flags, key, value);
              return { key, value };
            });

            await Promise.all(promises);
          } catch (error) {
            console.error("Error refreshing flags:", error);
          } finally {
            this.flagsLoading = false;
          }
        },
      },
    });

    // Global directive for conditional rendering based on flags
    Vue.directive("flag", {
      bind(el, binding, vnode) {
        const { value, modifiers } = binding;
        const { context } = vnode;

        if (typeof value === "string") {
          // v-flag="'flag-name'"
          context.$watchFlag(value).then(() => {
            const flagValue = context.flags[value];
            el.style.display = flagValue ? "" : "none";
          });
        } else if (typeof value === "object") {
          // v-flag="{ key: 'flag-name', default: false }"
          const { key, default: defaultValue = false } = value;
          context.$watchFlag(key, defaultValue).then(() => {
            const flagValue = context.flags[key];

            if (modifiers.hide) {
              // v-flag.hide - hide when flag is true
              el.style.display = flagValue ? "none" : "";
            } else {
              // default behavior - show when flag is true
              el.style.display = flagValue ? "" : "none";
            }
          });
        }
      },

      update(el, binding, vnode) {
        const { value, modifiers } = binding;
        const { context } = vnode;

        let flagValue;
        if (typeof value === "string") {
          flagValue = context.flags[value];
        } else if (typeof value === "object") {
          flagValue = context.flags[value.key];
        }

        if (modifiers.hide) {
          el.style.display = flagValue ? "none" : "";
        } else {
          el.style.display = flagValue ? "" : "none";
        }
      },
    });

    // Global component for conditional rendering
    Vue.component("FeatureFlag", {
      props: {
        flag: {
          type: String,
          required: true,
        },
        defaultValue: {
          type: [Boolean, String, Number, Object],
          default: false,
        },
        invert: {
          type: Boolean,
          default: false,
        },
        // Add loading prop to show loading state externally
        showLoading: {
          type: Boolean,
          default: false,
        },
      },
      data() {
        return {
          flagValue: this.defaultValue,
          loading: true,
          error: null,
        };
      },
      async mounted() {
        await this.loadFlag();
      },
      methods: {
        async loadFlag() {
          this.loading = true;
          this.error = null;

          try {
            this.flagValue = await this.$getFlag(this.flag, this.defaultValue);
          } catch (error) {
            console.error(`Error loading flag ${this.flag}:`, error);
            this.flagValue = this.defaultValue;
            this.error = error;
          } finally {
            this.loading = false;
          }
        },
        async refreshFlag() {
          await this.loadFlag();
        },
      },
      computed: {
        shouldShow() {
          if (this.loading && this.showLoading) return false;
          return this.invert ? !this.flagValue : !!this.flagValue;
        },
        isLoading() {
          return this.loading;
        },
        hasError() {
          return !!this.error;
        },
      },
      render(h) {
        // Show loading state if requested
        if (this.loading && this.showLoading) {
          return this.$slots.loading || h("div", { class: "feature-flag-loading" }, "Loading...");
        }

        // Show error state if there's an error and error slot exists
        if (this.error && this.$slots.error) {
          return this.$slots.error;
        }

        // Show content based on flag value
        if (this.shouldShow) {
          return this.$slots.default || null;
        }

        return this.$slots.fallback || null;
      },
    });
  },
};

export default LaunchDarklyPlugin;
